import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CITY } from "@/constants/testIds";
import {
  Cloud, CloudRain, CloudSnow, CloudLightning, Sun, CloudSun, CloudFog, Wind, Search, X, MapPin,
} from "lucide-react";

const PINNED_KEY = "gawx_pinned_zips";

function iconFor(condition = "") {
  const c = (condition || "").toLowerCase();
  if (c.includes("thunder") || c.includes("storm")) return <CloudLightning className="h-8 w-8 text-[#ffd60a]" />;
  if (c.includes("snow")) return <CloudSnow className="h-8 w-8 text-sky-300" />;
  if (c.includes("rain") || c.includes("shower")) return <CloudRain className="h-8 w-8 text-sky-300" />;
  if (c.includes("fog") || c.includes("mist")) return <CloudFog className="h-8 w-8 text-gray-300" />;
  if (c.includes("wind")) return <Wind className="h-8 w-8 text-gray-300" />;
  if (c.includes("partly") || c.includes("mostly")) return <CloudSun className="h-8 w-8 text-[#ffd60a]" />;
  if (c.includes("sun") || c.includes("clear") || c.includes("fair")) return <Sun className="h-8 w-8 text-[#ffd60a]" />;
  return <Cloud className="h-8 w-8 text-gray-300" />;
}

function CityCard({ city, pinned = false, onRemove }) {
  return (
    <div
      data-testid={CITY(city.id).card}
      className="glass lift group relative overflow-hidden rounded-sm p-4"
    >
      <div className="absolute right-3 top-3 opacity-90">{iconFor(city.condition)}</div>
      {pinned && (
        <button
          data-testid={`city-remove-${city.id}`}
          onClick={onRemove}
          aria-label={`Remove ${city.name}`}
          className="absolute left-3 top-3 z-10 flex h-6 w-6 items-center justify-center rounded-full border border-white/15 bg-black/60 text-gray-300 opacity-0 transition-opacity hover:border-[#1f63c9] hover:text-[#1f63c9] group-hover:opacity-100"
        >
          <X className="h-3 w-3" />
        </button>
      )}
      <div className="font-oswald text-xs uppercase tracking-widest text-gray-400">
        {pinned ? (
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3 w-3 text-[#ffd60a]" /> Pinned · {city.zip || "Georgia"}
          </span>
        ) : (
          "Georgia"
        )}
      </div>
      <div className="mt-1 font-oswald text-lg font-bold uppercase tracking-wider text-white line-clamp-1">
        {pinned ? (city.place || city.name) : city.name}
      </div>
      <div
        data-testid={CITY(city.id).temp}
        className="mt-6 font-oswald text-5xl font-bold text-white"
      >
        {city.current_temp != null ? city.current_temp : "—"}
        <span className="ml-0.5 align-top text-lg text-gray-400">°F</span>
      </div>
      <div className="mt-1 line-clamp-1 text-xs text-gray-300">{city.condition}</div>
      <div className="mt-3 flex items-center justify-between text-xs text-gray-400">
        <span className="font-mono">H {city.high ?? "—"}° / L {city.low ?? "—"}°</span>
        <span className="font-mono">{city.wind || "—"}</span>
      </div>
    </div>
  );
}

export default function CityConditions() {
  const [cities, setCities] = useState([]);
  const [pinned, setPinned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zip, setZip] = useState("");
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    api.get("/weather/cities")
      .then((r) => setCities(r.data))
      .catch(() => setCities([]))
      .finally(() => setLoading(false));

    // Restore pinned ZIPs and refresh them
    let stored = [];
    try { stored = JSON.parse(localStorage.getItem(PINNED_KEY) || "[]"); } catch { stored = []; }
    if (Array.isArray(stored) && stored.length) {
      Promise.all(
        stored.map((z) =>
          api.get(`/weather/by-zip/${z}`).then((r) => r.data).catch(() => null)
        )
      ).then((rows) => setPinned(rows.filter(Boolean)));
    }
  }, []);

  const persist = (list) => {
    const zips = list.map((c) => c.zip).filter(Boolean);
    localStorage.setItem(PINNED_KEY, JSON.stringify(zips));
  };

  const addZip = async (e) => {
    e?.preventDefault();
    const z = zip.trim();
    if (!/^\d{5}$/.test(z)) {
      toast.error("Enter a 5-digit ZIP code");
      return;
    }
    if (pinned.some((c) => c.zip === z)) {
      toast.info("That ZIP is already pinned");
      setZip("");
      return;
    }
    setSearching(true);
    try {
      const { data } = await api.get(`/weather/by-zip/${z}`);
      const next = [data, ...pinned];
      setPinned(next);
      persist(next);
      setZip("");
      toast.success(`Pinned ${data.place || z}`);
    } catch (err) {
      const msg = err.response?.data?.detail || "ZIP lookup failed";
      toast.error(typeof msg === "string" ? msg : "ZIP lookup failed");
    } finally {
      setSearching(false);
    }
  };

  const removeZip = (z) => {
    const next = pinned.filter((c) => c.zip !== z);
    setPinned(next);
    persist(next);
  };

  return (
    <section className="relative py-14" id="conditions">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="font-oswald text-xs uppercase tracking-[0.3em] text-[#1f63c9]">Statewide</div>
            <h2 className="font-oswald text-2xl font-bold uppercase tracking-wider text-white sm:text-3xl lg:text-4xl">
              Current Conditions
            </h2>
          </div>

          <form
            onSubmit={addZip}
            className="glass flex items-center gap-2 rounded-sm border-white/15 px-2 py-1.5"
          >
            <Search className="ml-1 h-4 w-4 text-gray-400" />
            <input
              data-testid="zip-search-input"
              type="text"
              inputMode="numeric"
              maxLength={5}
              placeholder="Georgia ZIP (e.g. 30303)"
              value={zip}
              onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))}
              className="w-44 bg-transparent px-1 py-1 text-sm text-white placeholder-gray-500 outline-none"
            />
            <button
              type="submit"
              disabled={searching}
              data-testid="zip-search-submit"
              className="rounded-sm bg-[#1f63c9] px-3 py-1.5 font-oswald text-[11px] font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#2a74d8] disabled:opacity-60"
            >
              {searching ? "…" : "Pin"}
            </button>
          </form>
        </div>

        {pinned.length > 0 && (
          <div
            data-testid="pinned-grid"
            className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6"
          >
            {pinned.map((c) => (
              <CityCard key={c.id} city={c} pinned onRemove={() => removeZip(c.zip)} />
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {loading &&
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-40 animate-pulse rounded border border-white/10 bg-white/5" />
            ))}

          {!loading && cities.map((c) => <CityCard key={c.id} city={c} />)}
        </div>

        <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
          <div className="font-mono uppercase tracking-widest">
            Source: National Weather Service
          </div>
          {pinned.length > 0 && (
            <div className="font-mono">
              {pinned.length} pinned · saved on this device
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
